import { ShareType, ShareTypeCode } from "@/common/share";
import {
  httpDelete,
  HttpEvent,
  HttpEventCallbacks,
  httpGet,
  httpPost,
  httpPut,
} from "@/events/eventsDomain";
import { toQueryString } from "@/utils/queryParams";

export type CapTableStakeholder = {
  shareholderId?: number;
  holderId?: string;
  name?: string;
  isCompany: boolean;
};

export type CapTableRow = {
  stakeholder: CapTableStakeholder;
  sharesByClass: Record<string, number>;
  optionsByClass: Record<string, number>;
  optionsWithoutClass: number;
  shares: number;
  options: number;
  fullyDiluted: number;
  basicShare: number;
  fdShare: number;
};

export type CapTablePoolLine = {
  planId: string;
  planName: string;
  poolSize: number;
  used: number;
  unallocated: number;
  fdShare: number;
};

export type CapTableTotals = {
  sharesByClass: Record<string, number>;
  shares: number;
  optionsByClass: Record<string, number>;
  optionsWithoutClass: number;
  options: number;
  unallocatedPool: number;
  fullyDiluted: number;
};

export type CapTableWarning =
  | { type: "OPTIONS_WITHOUT_SHARE_CLASS"; options: number }
  | { type: "PROGRAM_ABOVE_POOL"; planId: string; planName: string; optionsAbovePool: number };

export type CapTable = {
  asOf: string;
  basis: { includeUnallocatedPool: boolean };
  shareTypes: ShareType[];
  rows: CapTableRow[];
  ownShares: Record<string, number>;
  pools: CapTablePoolLine[];
  totals: CapTableTotals;
  warnings: CapTableWarning[];
};

export type Pricing = { type: "PRE_MONEY"; preMoney: number } | { type: "PRICE"; price: number };

export type RoundInvestorInput =
  | { type: "EXISTING"; shareholderId: number; amount: number }
  | { type: "NEW"; name: string; isCompany: boolean; amount: number };

export type RoundShareClassInput =
  | { type: "EXISTING"; shareTypeId: number }
  | { type: "NEW"; shareTypeCode: ShareTypeCode };

export type PoolProgramInput = { type: "EXISTING"; planId: string } | { type: "NEW"; name: string };

export type PoolPlacement = "PRE_MONEY" | "POST_MONEY";

export type PoolTargetInput = {
  unallocatedShare: number;
  placement: PoolPlacement;
  program: PoolProgramInput;
};

export type RoundInput = {
  pricing: Pricing;
  investors: RoundInvestorInput[];
  shareClass: RoundShareClassInput;
  poolTarget?: PoolTargetInput | null;
  priceDecimals: number;
};

export type InvestorAllocation = {
  stakeholder: CapTableStakeholder;
  investment: number;
  shares: number;
  amountPaid: number;
  residual: number;
};

export type PreEmptiveEntitlement = {
  stakeholder: CapTableStakeholder;
  shares: number;
};

export type SimulationIssue =
  | { type: "PRICE_BELOW_NOMINAL"; level: "BLOCK"; price: number; nominalValue: number }
  | {
      type: "POOL_TOP_UP_ABOVE_AUTHORISATION_LIMIT";
      level: "WARN";
      poolTopUp: number;
      limit: number;
    }
  | { type: "PRE_EMPTIVE_RIGHTS_SET_ASIDE"; level: "INFO"; entitlements: PreEmptiveEntitlement[] };

export type RoundResult = {
  pricePerShare: number;
  currency: string;
  preMoney: number;
  postMoney: number;
  shareType: ShareType;
  investors: InvestorAllocation[];
  newShares: number;
  amountRaised: number;
  poolTopUp: number;
  dilution: number;
  capitalIncrease: number;
  sharePremium: number;
  before: CapTable;
  after: CapTable;
  issues: SimulationIssue[];
};

export type RoundInputError = { message: string } & (
  | { error: "InvalidPriceDecimals" }
  | { error: "ValuationNotPositive" }
  | { error: "NothingToValue" }
  | { error: "PriceRoundsToZero" }
  | { error: "NegativeInvestment"; investorIndex: number }
  | { error: "BlankInvestorName"; investorIndex: number }
  | { error: "ShareholderNotInCapTable"; shareholderId: number }
  | { error: "ShareClassNotInCapTable"; shareTypeId: number }
  | { error: "ShareClassExists"; shareTypeCode: ShareTypeCode }
  | { error: "InvalidPoolTarget" }
  | { error: "ProgramWithoutPool"; planId: string }
  | { error: "BlankProgramName" }
  | { error: "PoolTargetUnreachable" }
);

export type ScenarioError = RoundInputError | { error: "BlankName"; message: string };

export type SimulateRequest = { date?: string; input: RoundInput };

export type ScenarioRequest = { name: string; date?: string; input: RoundInput };

export type ScenarioSummary = {
  id: string;
  name: string;
  baseDate?: string;
  pricePerShare?: number;
  postMoney?: number;
  currency?: string;
  isRegisterChanged: boolean;
  error?: RoundInputError;
  updatedAt: string;
};

export type Scenario = {
  id: string;
  name: string;
  baseDate?: string;
  input: RoundInput;
  baseline: { asOf: string; fullyDiluted: number };
  isRegisterChanged: boolean;
  result?: RoundResult;
  error?: RoundInputError;
  createdAt: string;
  updatedAt: string;
};

export type CapTableQuery = {
  date?: string;
  includePool: boolean;
};

export type CapTableApiClient = {
  getCapTable: (
    orgNo: string,
    query: CapTableQuery,
    params: HttpEventCallbacks<CapTable>,
  ) => HttpEvent<CapTable>;
  simulate: (
    orgNo: string,
    request: SimulateRequest,
    params: HttpEventCallbacks<RoundResult>,
  ) => HttpEvent<RoundResult>;
  getScenarios: (
    orgNo: string,
    params: HttpEventCallbacks<ScenarioSummary[]>,
  ) => HttpEvent<ScenarioSummary[]>;
  getScenario: (
    orgNo: string,
    scenarioId: string,
    params: HttpEventCallbacks<Scenario>,
  ) => HttpEvent<Scenario>;
  createScenario: (
    orgNo: string,
    request: ScenarioRequest,
    params: HttpEventCallbacks<Scenario>,
  ) => HttpEvent<Scenario>;
  updateScenario: (
    orgNo: string,
    scenarioId: string,
    request: ScenarioRequest,
    params: HttpEventCallbacks<Scenario>,
  ) => HttpEvent<Scenario>;
  deleteScenario: (
    orgNo: string,
    scenarioId: string,
    params: HttpEventCallbacks<void>,
  ) => HttpEvent<void>;
};

const scenarioUrl = (orgNo: string, scenarioId: string) =>
  `/cap-table/${orgNo}/scenarios/${encodeURIComponent(scenarioId)}`;

export const capTableApiClient: CapTableApiClient = {
  getCapTable: (orgNo, { date, includePool }, params) =>
    httpGet({ url: `/cap-table/${orgNo}${toQueryString({ date, includePool })}`, ...params }),
  simulate: (orgNo, request, params) =>
    httpPost({ url: `/cap-table/${orgNo}/simulate`, data: request, ...params }),
  getScenarios: (orgNo, params) => httpGet({ url: `/cap-table/${orgNo}/scenarios`, ...params }),
  getScenario: (orgNo, scenarioId, params) =>
    httpGet({ url: scenarioUrl(orgNo, scenarioId), ...params }),
  createScenario: (orgNo, request, params) =>
    httpPost({ url: `/cap-table/${orgNo}/scenarios`, data: request, ...params }),
  updateScenario: (orgNo, scenarioId, request, params) =>
    httpPut({ url: scenarioUrl(orgNo, scenarioId), data: request, ...params }),
  deleteScenario: (orgNo, scenarioId, params) =>
    httpDelete({ url: scenarioUrl(orgNo, scenarioId), ...params }),
};
